import React, { useEffect, useRef, useState } from 'react';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Button, TextField, Typography } from '@mui/material';

import { useLanguage } from '../../hooks';
import { VerificationCodeLock } from '../icons';

const CODE_LENGTH = 6;

const customStyles = {
  lockIcon: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '40px',
    height: '163px',
    '@media (max-width: 600px)': {
      marginBottom: '24px',
    },
  },
  codeInput: {
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    backgroundColor: '#FAFBFF',
    borderColor: '#DDE0E5',
    borderRadius: '1px',
    '& .MuiOutlinedInput-root': {
      width: '55px',
      height: '55px',
      '& .MuiInputBase-input': {
        textAlign: 'center',
        alignSelf: 'center',
        padding: '0px',
      },
      '& fieldset': {
        borderColor: '#E5E7EB',
      },
      '&:hover fieldset': {
        borderColor: '#D1D5DB',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#3B82F6',
      },
      '@media (max-width: 600px)': {
        width: '40px',
        height: '40px',
      },
    },
  },
  codeInputText: {
    fontFamily: 'Noto Sans',
    fontWeight: 500,
    fontStyle: 'Medium',
    fontSize: '30px',
    lineHeight: '40px',
    letterSpacing: '0%',
  },
  errorText: {
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    color: '#EF4444',
    alignSelf: 'center',
  },
  boxContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
  },
  verifyButton: {
    borderRadius: '18px',
    padding: '8px 18px',
    height: '40px',
    width: '100%',
    fontFamily: 'Noto Sans',
    fontWeight: 500,
    fontStyle: 'Medium',
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0%',
  },
  mainHeading: {
    fontFamily: 'Noto Sans',
    fontWeight: 500,
    fontStyle: 'Medium',
    fontSize: '24px',
    lineHeight: '32px',
    letterSpacing: '0%',
    textAlign: 'center',
    '@media (max-width: 600px)': {
      fontSize: '16px',
      lineHeight: '24px',
    },
  },
  description: {
    fontFamily: 'Noto Sans',
    fontWeight: 400,
    fontStyle: 'Regular',
    fontSize: '14px',
    lineHeight: '22px',
    letterSpacing: '0%',
    textAlign: 'center',
  },
};

const initialCode = Array.from({ length: CODE_LENGTH }, () => '');

interface MfaVerifyCodeProps {
  onVerify: (code: string) => Promise<boolean>;
  error?: string;
  isLoading?: boolean;
}

export const MfaVerifyCode = ({
  onVerify,
  error: externalError,
  isLoading = false,
}: MfaVerifyCodeProps) => {
  const [code, setCode] = useState<string[]>(initialCode);
  const [internalError, setInternalError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const lang = useLanguage();

  // Focus the first input box when component mounts
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleInputChange = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    // Handle empty input (backspace)
    if (value === '') {
      setCode((prevCode) => [...prevCode].map((val, i) => (i === index ? '' : val)));
      return;
    }

    // Handle paste of 6-digit code
    if (value.length === 6 && /^\d{6}$/.test(value)) {
      const digits = value.split('');
      setCode(digits);
      // Focus on the last input field
      inputRefs.current[5]?.focus();
      return;
    }

    // Only allow single digits
    if (value.length > 1) {
      const lastChar = value.slice(-1);
      if (isNaN(Number(lastChar))) return;

      setCode((prevCode) => [...prevCode].map((val, i) => (i === index ? lastChar : val)));
      if (index < code.length - 1) inputRefs.current[index + 1]?.focus();
    } else if (value.length === 1) {
      if (isNaN(Number(value))) return;

      setCode((prevCode) => [...prevCode].map((val, i) => (i === index ? value : val)));
      if (index < code.length - 1) inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number) => (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Only handle backspace for navigation
    if (event.key === 'Backspace' && code[index] === '') {
      if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handlePaste = () => (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text');

    // Check if pasted data is a 6-digit number
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setCode(digits);
      // Focus on the last input field
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerify = async () => {
    const codeString = code.join('');
    if (codeString.length !== CODE_LENGTH) return;

    setIsVerifying(true);
    setInternalError(null);

    try {
      const success = await onVerify(codeString);
      if (!success) {
        setInternalError(lang.mfa.verifyCodeInvalid);
      }
    } catch (error) {
      setInternalError(lang.mfa.verifyCodeError);
    } finally {
      setIsVerifying(false);
    }
  };

  const codeDigitCount = code.filter((value) => value !== '').length;
  const verifyDisabled = codeDigitCount < CODE_LENGTH || isLoading || isVerifying;
  const displayError = externalError ? lang.mfa.verifyCodeCognitoError : internalError;

  return (
    <>
      <VerificationCodeLock sx={customStyles.lockIcon} />
      <Typography sx={customStyles.mainHeading}>{lang.mfa.verifyCode}</Typography>
      <Typography sx={customStyles.description}>{lang.mfa.verifyCodeDescription}</Typography>

      <Box sx={customStyles.boxContainer}>
        {code.map((value, index) => (
          <TextField
            key={index}
            variant='outlined'
            value={value}
            onChange={handleInputChange(index)}
            onKeyDown={handleKeyDown(index)}
            onPaste={handlePaste()}
            inputProps={{
              maxLength: 1,
              autoComplete: 'off',
              inputMode: 'numeric',
              pattern: '[0-9]*',
              sx: customStyles.codeInputText,
            }}
            sx={customStyles.codeInput}
            inputRef={(el) => (inputRefs.current[index] = el)}
            disabled={isLoading || isVerifying}
          />
        ))}
      </Box>

      {displayError && (
        <Typography
          variant='body1'
          color='#EF4444'
          sx={customStyles.errorText}
          gutterBottom
        >
          <ErrorOutlineIcon fontSize='small' />
          {displayError}
        </Typography>
      )}

      <Button
        variant='contained'
        color='primary'
        sx={customStyles.verifyButton}
        disabled={verifyDisabled}
        onClick={handleVerify}
      >
        {isVerifying ? lang.mfa.verifying : lang.mfa.finish}
      </Button>
    </>
  );
};
